import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { taskTemplateService, dailyTaskService } from '../../services/taskService';
import { siteService } from '../../services/siteService';
import { workerService } from '../../services/workerService';
import { taskCompletionPhotosService } from '../../services/taskCompletionPhotosService';
import TaskDetailModal from '../../components/TaskDetailModal';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { useAuth } from '../../contexts/AuthContext';
import { TASK_CATEGORIES } from '../../data/taskData';

import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';
import { useLanguage } from '../../contexts/LanguageContext';
import { sanitizeError } from '../../utils/sanitizeError';

const CATEGORY_COLORS = {
  Safety: 'bg-red-100 text-red-700 border-red-200',
  Quality: 'bg-blue-100 text-blue-700 border-blue-200',
  Operations: 'bg-amber-100 text-amber-700 border-amber-200',
  Documentation: 'bg-purple-100 text-purple-700 border-purple-200',
};

const CATEGORY_DOT = {
  Safety: 'bg-red-500',
  Quality: 'bg-blue-500',
  Operations: 'bg-amber-500',
  Documentation: 'bg-purple-500',
};

const ProgressBar = ({ value, color = 'bg-accent' }) => (
  <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
  </div>
);

const TaskPlanner = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { t } = useLanguage();
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [loadingInit, setLoadingInit] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const [selectedSite, setSelectedSite] = useState('');
  const { status: selectedSiteStatus, isReadOnly: selectedSiteReadOnly } = useSiteReadOnly(selectedSite || null);
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [siteWorkers, setSiteWorkers] = useState([]);
  const [loadingSiteWorkers, setLoadingSiteWorkers] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('all');
  // Due time applied to tasks added from a template. Editable; defaults to 09:00.
  const [templateDueTime, setTemplateDueTime] = useState('09:00');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTask, setCustomTask] = useState({ title: '', description: '', category: 'Safety', required: true, due_time: '09:00', photo_required: false, min_photos: 0 });
  const [assignTo, setAssignTo] = useState('whole_crew');
  const [activeTab, setActiveTab] = useState('plan');
  const [publishedMsg, setPublishedMsg] = useState('');
  const [publishError, setPublishError] = useState('');
  const [photoEvidenceTask, setPhotoEvidenceTask] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedSite && selectedDate) {
      loadExistingTasks();
    }
  }, [selectedSite, selectedDate]);

  useEffect(() => {
    if (selectedSite) {
      loadSiteWorkers(selectedSite);
      setSelectedWorkers([]);
    }
  }, [selectedSite]);

  const loadSiteWorkers = async (siteId) => {
    try {
      setLoadingSiteWorkers(true);
      const result = await siteService?.getWorkersBySite(siteId);
      if (!result?.error && result?.data?.length > 0) {
        setSiteWorkers(result?.data);
      } else {
        // Fallback: use all workers if no site-specific workers found
        setSiteWorkers(workers);
      }
    } catch (e) {
      setSiteWorkers(workers);
    } finally {
      setLoadingSiteWorkers(false);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoadingInit(true);
      const [sitesResult, workersResult, templatesResult] = await Promise.all([
        siteService?.getSelectableSites(),
        workerService?.getAllWorkers(),
        taskTemplateService?.getAll(),
      ]);
      const siteList = sitesResult?.data || [];
      const workerList = (workersResult?.data || [])?.filter(w => w?.role === 'worker' || w?.role === 'site_manager');
      setSites(siteList);
      setWorkers(workerList);
      setTemplates(templatesResult?.data || []);
      if (siteList?.length > 0) {
        // Prefer the site with the most recent task activity across the
        // company — matches the user's "show me where I just worked" mental
        // model. `getSelectableSites` orders by created_at DESC, so siteList[0]
        // is always the newest site, which is systematically the wrong
        // default when a company creates sites ahead of scheduling work.
        // Fall back to siteList[0] when the company has no tasks anywhere
        // yet, or when the active-site lookup doesn't match any loaded site.
        const activityResult = await dailyTaskService?.getSiteWithLatestActivity(userProfile?.company_id);
        if (activityResult?.error) {
          console.error('[TaskPlanner] getSiteWithLatestActivity failed:', activityResult?.error);
        }
        const activeSiteId = activityResult?.data;
        const defaultSiteId = (activeSiteId && siteList?.some((s) => s?.id === activeSiteId))
          ? activeSiteId
          : siteList?.[0]?.id;
        setSelectedSite(defaultSiteId);
        // Default the date picker to the most recent day that actually has
        // tasks for this site, so the Live Progress tab doesn't open on a
        // blank day. Falls back to today if the site has no tasks yet.
        const latest = await dailyTaskService?.getLatestTaskDate(defaultSiteId);
        if (latest?.data) setSelectedDate(latest?.data);
      }
    } catch (e) {
      console.error('[TaskPlanner] loadInitialData failed:', e?.message);
      setLoadError('Failed to load task planner data. Please refresh.');
    } finally {
      setLoadingInit(false);
    }
  };

  const loadExistingTasks = async () => {
    const result = await dailyTaskService?.getForSite(selectedSite, selectedDate);
    if (result?.error) {
      const err = result?.error;
      console.error('[TaskPlanner] loadExistingTasks failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301') {
        setLoadError(`Permission denied loading tasks: ${err?.message}`);
      } else {
        setLoadError(`Failed to load tasks: ${err?.message ?? 'unknown error'}`);
      }
      return;
    }
    setAssignedTasks(result?.data || []);
  };

  const siteTasks = useMemo(() => assignedTasks?.filter(t => t?.site_id === selectedSite && t?.date === selectedDate), [assignedTasks, selectedSite, selectedDate]);

  const filteredTemplates = useMemo(() => {
    return templates?.filter(t => {
      const matchSearch = !templateSearch || t?.title?.toLowerCase()?.includes(templateSearch?.toLowerCase());
      const matchCat = templateCategory === 'all' || t?.default_category === templateCategory;
      return matchSearch && matchCat;
    });
  }, [templates, templateSearch, templateCategory]);

  const workerProgress = useMemo(() => {
    return workers?.map(w => {
      const wTasks = siteTasks?.filter(t => t?.assigned_to_user_id === w?.id);
      const done = wTasks?.filter(t => t?.status === 'completed')?.length;
      const rate = wTasks?.length > 0 ? Math.round((done / wTasks?.length) * 100) : 0;
      return { ...w, tasks: wTasks?.length, done, rate };
    })?.filter(w => w?.tasks > 0);
  }, [siteTasks, workers]);

  const toggleWorker = (id) => {
    setSelectedWorkers(prev => prev?.includes(id) ? prev?.filter(w => w !== id) : [...prev, id]);
  };

  const getTargetWorkers = () => {
    if (assignTo === 'whole_crew') return (siteWorkers?.length > 0 ? siteWorkers : workers)?.map(w => w?.id);
    if (assignTo === 'individual') return selectedWorkers?.length > 0 ? selectedWorkers?.slice(0, 1) : [];
    if (assignTo === 'multiple') return selectedWorkers?.length > 0 ? selectedWorkers : [];
    return (siteWorkers?.length > 0 ? siteWorkers : workers)?.map(w => w?.id);
  };

  const addFromTemplate = (template) => {
    const targetWorkers = getTargetWorkers();
    const newTasks = targetWorkers?.map((wId, i) => ({
      id: `local-${Date.now()}-${i}`,
      site_id: selectedSite,
      date: selectedDate,
      created_by: user?.id,
      assigned_to_user_id: wId,
      title: template?.title,
      description: template?.description,
      category: template?.default_category,
      required: template?.default_required,
      status: 'pending',
      completed_at: null,
      completed_by: null,
      notes: '',
      photo_required: template?.photo_required,
      photo_url: null,
      due_time: templateDueTime || '09:00',
      _isLocal: true,
    }));
    setAssignedTasks(prev => [...prev, ...newTasks]);
  };

  const addCustomTask = async () => {
    if (!customTask?.title?.trim()) return;
    const targetWorkers = getTargetWorkers();
    if (!targetWorkers?.length) {
      setPublishError('No workers available to assign. Please select a site with workers first.');
      return;
    }
    const tasksToInsert = targetWorkers?.map(wId => ({
      site_id: selectedSite,
      date: selectedDate,
      created_by: user?.id,
      assigned_to_user_id: wId,
      company_id: userProfile?.company_id || null,
      ...customTask,
      status: 'pending',
      completed_at: null,
      completed_by: null,
      photo_url: null,
    }));
    try {
      setPublishError('');
      const result = await dailyTaskService?.createMany(tasksToInsert);
      // dailyTaskService.createMany still returns `error` as a plain string
      // (pre-Phase-5 service shape). Wrap the string into an Error so the
      // catch block's `err?.message` works. Once taskService is migrated to
      // the canonical { code, message, ... } shape, this can simplify to
      // `throw result.error`.
      if (result?.error) throw typeof result.error === 'string' ? new Error(result.error) : result.error;
      setAssignedTasks(prev => [...prev, ...(result?.data || [])]);
      setCustomTask({ title: '', description: '', category: 'Safety', required: true, due_time: '09:00', photo_required: false, min_photos: 0 });
      setShowCustomForm(false);
    } catch (err) {
      setPublishError(sanitizeError(err?.message, 'Failed to add task'));
    }
  };

  const removeTask = async (taskId) => {
    const task = assignedTasks?.find(t => t?.id === taskId);
    setAssignedTasks(prev => prev?.filter(t => t?.id !== taskId));
    if (task && !task?._isLocal) {
      await dailyTaskService?.delete(taskId);
    }
  };

  const handlePublish = async () => {
    if (selectedSiteReadOnly) {
      setPublishError('This site is completed — please reactivate to publish tasks.');
      return;
    }
    const localTasks = siteTasks?.filter(t => t?._isLocal);
    if (localTasks?.length === 0) {
      setPublishedMsg('All tasks already saved.');
      setTimeout(() => setPublishedMsg(''), 3000);
      return;
    }
    try {
      setPublishing(true);
      setPublishError('');
      const tasksToSave = localTasks?.map(t => ({
        ...t,
        company_id: userProfile?.company_id || null,
      }));
      const result = await dailyTaskService?.createMany(tasksToSave);
      // dailyTaskService.createMany still returns `error` as a plain string
      // (pre-Phase-5 service shape). Wrap the string into an Error so the
      // catch block's `err?.message` works. Once taskService is migrated to
      // the canonical { code, message, ... } shape, this can simplify to
      // `throw result.error`.
      if (result?.error) throw typeof result.error === 'string' ? new Error(result.error) : result.error;
      // Replace local tasks with saved ones
      const savedIds = new Set(localTasks?.map(t => t?.id));
      setAssignedTasks(prev => [
        ...prev?.filter(t => !savedIds?.has(t?.id)),
        ...(result?.data || []),
      ]);
      const siteName = sites?.find(s => s?.id === selectedSite)?.site_name || 'site';
      setPublishedMsg(`${localTasks?.length} task${localTasks?.length !== 1 ? 's' : ''} published for ${siteName} on ${selectedDate}`);
      setTimeout(() => setPublishedMsg(''), 6000);
    } catch (err) {
      setPublishError(sanitizeError(err?.message, 'Failed to publish tasks'));
    } finally {
      setPublishing(false);
    }
  };

  const getBarColor = (r) => r >= 80 ? 'bg-emerald-500' : r >= 50 ? 'bg-amber-500' : 'bg-red-500';

  if (loadingInit) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="bg-card border-b border-border">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-5">
              <div className="h-7 bg-muted rounded w-48 animate-pulse mb-2" />
              <div className="h-4 bg-muted rounded w-72 animate-pulse" />
            </div>
          </div>
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 bg-card rounded-xl border border-border p-5">
                <div className="h-5 bg-muted rounded w-32 mb-4 animate-pulse" />
                <Skeleton variant="list-row" count={5} />
              </div>
              <div className="lg:col-span-3 bg-card rounded-xl border border-border p-5">
                <div className="h-5 bg-muted rounded w-40 mb-4 animate-pulse" />
                <Skeleton variant="list-row" count={4} />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Task Planner — SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-4">
            <SubscriptionReadOnlyBanner />
            <SiteReadOnlyBanner status={selectedSiteStatus} siteId={selectedSite} className="mt-3" />
          </div>
          {loadError && (
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-4">
              <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3">
                <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium">{loadError}</p>
              </div>
            </div>
          )}
          {/* Page Header */}
          <div className="bg-card border-b border-border">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
              <div className="flex items-center justify-between py-5">
                <div>
                  <h1 className="text-2xl font-bold text-foreground font-heading">Task Planner</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">Create and assign daily tasks to workers by site</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" iconName="BarChart2" iconPosition="left" onClick={() => navigate('/task-analytics-dashboard')}>Analytics</Button>
                  <button
                    onClick={handlePublish}
                    disabled={publishing}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-accent text-white font-semibold text-sm shadow-lg shadow-accent/25 hover:bg-accent/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <Icon name={publishing ? 'Loader2' : 'Send'} size={16} className={publishing ? 'animate-spin' : ''} />
                    {publishing ? 'Publishing...' : 'Publish Tasks'}
                  </button>
                </div>
              </div>
              {/* Tabs */}
              <div className="flex items-center gap-1">
                {[{ id: 'plan', label: 'Plan Tasks', icon: 'ClipboardList' }, { id: 'progress', label: 'Live Progress', icon: 'Activity' }]?.map(tab => (
                  <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab?.id ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                    <Icon name={tab?.icon} size={15} />{tab?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {publishedMsg && (
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                <Icon name="CheckCircle2" size={18} className="text-emerald-600" />
                <p className="text-sm font-semibold text-emerald-700">{publishedMsg}</p>
              </div>
            </div>
          )}

          {publishError && (
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pt-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={18} className="text-red-600" />
                <p className="text-sm font-semibold text-red-700">{publishError}</p>
              </div>
            </div>
          )}

          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6">
            {/* Empty state: no sites */}
            {sites?.length === 0 && !loadingInit && (
              <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-8 text-center">
                <Icon name="Building2" size={40} className="text-muted-foreground mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-foreground mb-2">No sites yet</h2>
                <p className="text-sm text-muted-foreground mb-6">Create your first site before you can assign tasks to workers.</p>
                <Button variant="default" onClick={() => navigate('/site-setup-wizard')}>
                  <Icon name="Plus" size={16} className="mr-1.5" />
                  Create Your First Site
                </Button>
              </div>
            )}

            {/* Site + Date + Workers selector */}
            <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-5 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Site</label>
                  <select value={selectedSite} onChange={e => setSelectedSite(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                    {sites?.length === 0 && <option value="">No sites available</option>}
                    {sites?.map(s => <option key={s?.id} value={s?.id}>{s?.site_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Date</label>
                  <input type="date" value={selectedDate} onChange={e => setSelectedDate(e?.target?.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Assign To</label>
                  <select value={assignTo} onChange={e => { setAssignTo(e?.target?.value); setSelectedWorkers([]); }} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                    <option value="whole_crew">Whole Crew</option>
                    <option value="individual">Individual Worker</option>
                    <option value="multiple">Multiple Workers</option>
                  </select>
                </div>
              </div>
              {(assignTo === 'individual' || assignTo === 'multiple') && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {assignTo === 'individual' ? 'Select Worker' : 'Select Workers'}
                      {loadingSiteWorkers && <span className="ml-2 text-xs font-normal normal-case text-muted-foreground">Loading...</span>}
                    </p>
                    {assignTo === 'multiple' && siteWorkers?.length > 0 && (
                      <button
                        onClick={() => setSelectedWorkers(selectedWorkers?.length === siteWorkers?.length ? [] : siteWorkers?.map(w => w?.id))}
                        className="text-xs font-semibold text-accent hover:underline"
                      >
                        {selectedWorkers?.length === siteWorkers?.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(siteWorkers?.length > 0 ? siteWorkers : workers)?.map(w => {
                      const isSelected = selectedWorkers?.includes(w?.id);
                      const isDisabled = assignTo === 'individual' && selectedWorkers?.length === 1 && !isSelected;
                      return (
                        <button
                          key={w?.id}
                          onClick={() => {
                            if (assignTo === 'individual') {
                              setSelectedWorkers(isSelected ? [] : [w?.id]);
                            } else {
                              toggleWorker(w?.id);
                            }
                          }}
                          disabled={isDisabled}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            isSelected
                              ? 'bg-accent text-white border-accent'
                              : isDisabled
                              ? 'bg-muted text-muted-foreground border-border opacity-40 cursor-not-allowed'
                              : 'bg-background text-foreground border-border hover:border-accent'
                          }`}
                        >
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            isSelected ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            {w?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)}
                          </span>
                          {w?.full_name?.split(' ')?.[0]}
                          {isSelected && assignTo === 'multiple' && (
                            <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4L3.5 6L6.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {(siteWorkers?.length === 0 && !loadingSiteWorkers) && (
                      <p className="text-xs text-muted-foreground italic">No workers assigned to this site yet</p>
                    )}
                  </div>
                  {assignTo === 'multiple' && selectedWorkers?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">{selectedWorkers?.length} worker{selectedWorkers?.length !== 1 ? 's' : ''} selected</p>
                  )}
                </div>
              )}
            </div>

            {activeTab === 'plan' && (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Left: Template Library */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                    <div className="px-5 py-4 border-b border-border">
                      <h2 className="font-semibold text-foreground font-heading mb-3">Task Templates</h2>
                      <input
                        type="text"
                        placeholder="Search templates..."
                        value={templateSearch}
                        onChange={e => setTemplateSearch(e?.target?.value)}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent mb-2"
                      />
                      <div className="flex gap-1.5 flex-wrap">
                        {['all', ...TASK_CATEGORIES]?.map(cat => (
                          <button key={cat} onClick={() => setTemplateCategory(cat)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${templateCategory === cat ? 'bg-accent text-white border-accent' : 'bg-background text-muted-foreground border-border hover:border-accent'}`}>
                            {cat === 'all' ? 'All' : cat}
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <label htmlFor="template-due-time" className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          Due time
                        </label>
                        <input
                          id="template-due-time"
                          type="time"
                          value={templateDueTime}
                          onChange={e => setTemplateDueTime(e?.target?.value)}
                          className="rounded-lg border border-border bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                        />
                        <span className="text-xs text-muted-foreground">applied to tasks added from a template</span>
                      </div>
                    </div>
                    <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
                      {filteredTemplates?.length === 0 && (
                        <div className="p-6 text-center text-sm text-muted-foreground">No templates found</div>
                      )}
                      {filteredTemplates?.map(tpl => (
                        <div key={tpl?.id} className="px-5 py-3.5 hover:bg-muted/30 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${CATEGORY_DOT?.[tpl?.default_category] || 'bg-muted-foreground'}`} />
                                <p className="text-sm font-medium text-foreground truncate">{tpl?.title}</p>
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2">{tpl?.description}</p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${CATEGORY_COLORS?.[tpl?.default_category]}`}>{tpl?.default_category}</span>
                                {tpl?.default_required && <span className="text-xs text-red-500 font-medium">Mandatory</span>}
                                {tpl?.photo_required && <span className="text-xs text-amber-600 font-medium flex items-center gap-0.5"><Icon name="Camera" size={11} />Photo Required</span>}
                                <span className="text-xs text-muted-foreground">{tpl?.estimated_minutes}m</span>
                              </div>
                            </div>
                            <button onClick={() => addFromTemplate(tpl)} disabled={subscriptionReadOnly || selectedSiteReadOnly} className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent/10 hover:bg-accent text-accent hover:text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent/10 disabled:hover:text-accent">
                              <Icon name="Plus" size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Helper nudge bridging templates → custom task */}
                  <p className="text-sm text-muted-foreground px-1">
                    Can&apos;t find what you need? Create a custom task below.
                  </p>

                  {/* Custom Task */}
                  <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl border-2 border-emerald-500 shadow-elevation-2 overflow-hidden">
                    <button onClick={() => setShowCustomForm(!showCustomForm)} disabled={subscriptionReadOnly || selectedSiteReadOnly} className="w-full px-5 py-4 flex items-center justify-between bg-emerald-500 hover:bg-emerald-600 text-white transition-colors disabled:bg-emerald-300 disabled:cursor-not-allowed">
                      <div className="flex items-center gap-2">
                        <Icon name="PlusCircle" size={20} className="text-white" />
                        <span className="font-semibold text-white text-base">Create Custom Task</span>
                      </div>
                      <Icon name={showCustomForm ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-white" />
                    </button>
                    {showCustomForm && (
                      <div className="px-5 pb-5 space-y-3 border-t border-border pt-4">
                        <input type="text" placeholder="Task title *" value={customTask?.title} onChange={e => setCustomTask(p => ({ ...p, title: e?.target?.value }))} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent" />
                        <textarea placeholder="Description (optional)" value={customTask?.description} onChange={e => setCustomTask(p => ({ ...p, description: e?.target?.value }))} rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none" />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Category</label>
                            <select value={customTask?.category} onChange={e => setCustomTask(p => ({ ...p, category: e?.target?.value }))} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent">
                              {TASK_CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-muted-foreground mb-1">Due Time</label>
                            <input type="time" value={customTask?.due_time} onChange={e => setCustomTask(p => ({ ...p, due_time: e?.target?.value }))} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent" />
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={customTask?.required} onChange={e => setCustomTask(p => ({ ...p, required: e?.target?.checked }))} className="rounded border-border text-accent focus:ring-accent" />
                            <span className="text-sm text-foreground">Mandatory</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={customTask?.photo_required} onChange={e => setCustomTask(p => ({ ...p, photo_required: e?.target?.checked, min_photos: e?.target?.checked ? Math.max(p?.min_photos || 1, 1) : 0 }))} className="rounded border-border text-accent focus:ring-accent" />
                            <span className="text-sm text-foreground">Require photo(s) to complete</span>
                          </label>
                        </div>
                        {customTask?.photo_required && (
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">Minimum photos required</label>
                            <input type="number" min={1} max={10} value={customTask?.min_photos || 1} onChange={e => setCustomTask(p => ({ ...p, min_photos: Math.min(Math.max(parseInt(e?.target?.value, 10) || 1, 1), 10) }))} className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent" />
                          </div>
                        )}
                        <Button variant="primary" size="sm" onClick={addCustomTask} disabled={subscriptionReadOnly || selectedSiteReadOnly} className="w-full">Add Task</Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Assigned Tasks */}
                <div className="lg:col-span-3">
                  <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                      <div>
                        <h2 className="font-semibold text-foreground font-heading">Assigned Tasks</h2>
                        <p className="text-xs text-muted-foreground mt-0.5">{siteTasks?.length} tasks · {sites?.find(s => s?.id === selectedSite)?.site_name} · {selectedDate}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${siteTasks?.length > 0 ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>{siteTasks?.length} tasks</span>
                    </div>
                    {siteTasks?.length === 0 ? (
                      <EmptyState
                        icon="ClipboardList"
                        title="No tasks planned"
                        description="Add tasks from the template library or create a custom task for this site and date."
                        ctaLabel="Create Custom Task"
                        ctaAction={() => setShowCustomForm(true)}
                        ctaIcon="Plus"
                        className="py-10"
                      />
                    ) : (
                      <div className="divide-y divide-border max-h-[560px] overflow-y-auto">
                        {workers?.map(worker => {
                          const wTasks = siteTasks?.filter(t => t?.assigned_to_user_id === worker?.id);
                          if (wTasks?.length === 0) return null;
                          const initials = worker?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2);
                          return (
                            <div key={worker?.id}>
                              <div className="px-5 py-2.5 bg-muted/40 flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center"><span className="text-xs font-bold text-accent">{initials}</span></div>
                                <span className="text-xs font-semibold text-foreground">{worker?.full_name}</span>
                                <span className="text-xs text-muted-foreground">· {wTasks?.length} tasks</span>
                              </div>
                              {wTasks?.map(task => (
                                <div key={task?.id} className={`px-5 py-3 flex items-start gap-3 hover:bg-muted/20 transition-colors ${task?.status === 'completed' || task?.status === 'blocked' ? 'cursor-pointer' : ''}`} onClick={() => (task?.status === 'completed' || task?.status === 'blocked') && setPhotoEvidenceTask(task)}>
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${CATEGORY_DOT?.[task?.category] || 'bg-muted-foreground'}`} />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-foreground truncate">{task?.title}</p>
                                      {task?.required && <span className="text-xs text-red-500 font-medium flex-shrink-0">Mandatory</span>}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS?.[task?.category]}`}>{task?.category}</span>
                                      {task?.due_time && <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Icon name="Clock" size={10} />{task?.due_time}</span>}
                                      {task?.photo_required && <span className="text-xs text-amber-600 font-medium flex items-center gap-0.5"><Icon name="Camera" size={11} />{task?.min_photos > 1 ? `${task?.min_photos} Photos Required` : 'Photo Required'}</span>}
                                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${task?.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : task?.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'}`}>{task?.status}</span>
                                      {task?._isLocal ? (
                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                          <Icon name="FilePenLine" size={11} />
                                          Draft
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                          <Icon name="CheckCircle2" size={11} />
                                          Published
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <button onClick={() => removeTask(task?.id)} className="flex-shrink-0 w-7 h-7 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all">
                                    <Icon name="X" size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {siteTasks?.length > 0 && (
                      <>
                        {publishedMsg && (
                          <div className="px-5 pt-3">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3">
                              <Icon name="CheckCircle2" size={18} className="text-emerald-600 flex-shrink-0" />
                              <p className="text-sm font-semibold text-emerald-700">{publishedMsg}</p>
                            </div>
                          </div>
                        )}
                        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-muted-foreground">{siteTasks?.filter(t => t?.required)?.length} required · {siteTasks?.filter(t => !t?.required)?.length} optional</p>
                            {siteTasks?.filter(t => t?._isLocal)?.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full animate-pulse">
                                <Icon name="AlertCircle" size={12} />
                                {siteTasks?.filter(t => t?._isLocal)?.length} unpublished
                              </span>
                            )}
                          </div>
                          <button
                            onClick={handlePublish}
                            disabled={publishing}
                            className="inline-flex items-center gap-2 h-10 px-6 rounded-lg bg-accent text-white font-semibold text-sm shadow-lg shadow-accent/25 hover:bg-accent/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none"
                          >
                            <Icon name={publishing ? 'Loader2' : 'Send'} size={16} className={publishing ? 'animate-spin' : ''} />
                            {publishing ? 'Publishing...' : `Publish for ${selectedDate}`}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'progress' && (
              <div className="space-y-6">
                {siteTasks?.length === 0 && selectedDate === today && (
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <Icon name="Info" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      No tasks scheduled for today. Use the date picker to view another day, or plan tasks on the Plan Tasks tab.
                    </p>
                  </div>
                )}
                {/* Overall site progress */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {(() => {
                    const total = siteTasks?.length;
                    const done = siteTasks?.filter(t => t?.status === 'completed')?.length;
                    const blocked = siteTasks?.filter(t => t?.status === 'blocked')?.length;
                    const rate = total > 0 ? Math.round((done / total) * 100) : 0;
                    return [
                      { label: 'Total Tasks', value: total, color: 'text-foreground', bg: 'bg-muted/50' },
                      { label: 'Completed', value: done, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                      { label: 'Blocked', value: blocked, color: 'text-red-600', bg: 'bg-red-50' },
                      { label: 'Completion', value: `${rate}%`, color: rate >= 80 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600', bg: 'bg-accent/5' },
                    ]?.map(stat => (
                      <div key={stat?.label} className={`${stat?.bg} rounded-xl p-4 border border-border`}>
                        <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
                      </div>
                    ));
                  })()}
                </div>

                {/* Per-worker progress */}
                <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                  <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <h2 className="font-semibold text-foreground font-heading">Worker Progress</h2>
                    <button onClick={() => navigate('/task-analytics-dashboard')} className="text-xs font-semibold text-accent hover:underline flex items-center gap-1">Full Analytics <Icon name="ArrowRight" size={12} /></button>
                  </div>
                  {workerProgress?.length === 0 ? (
                    <div className="p-8 text-center"><p className="text-sm text-muted-foreground">No tasks assigned for this site/date</p></div>
                  ) : (
                    <div className="divide-y divide-border">
                      {workerProgress?.map(w => (
                        <div key={w?.id} className="px-5 py-4 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => navigate(`/task-completion-workflow?worker=${w?.id}`)}>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0"><span className="text-sm font-bold text-accent">{w?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)}</span></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1.5">
                                <div>
                                  <p className="text-sm font-semibold text-foreground">{w?.full_name}</p>
                                  <p className="text-xs text-muted-foreground">{w?.done}/{w?.tasks} tasks</p>
                                </div>
                                <span className={`text-sm font-bold ${w?.rate >= 80 ? 'text-emerald-600' : w?.rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{w?.rate}%</span>
                              </div>
                              <ProgressBar value={w?.rate} color={getBarColor(w?.rate)} />
                            </div>
                            <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Task list with status */}
                <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
                  <div className="px-5 py-4 border-b border-border">
                    <h2 className="font-semibold text-foreground font-heading">All Tasks — Live Status</h2>
                  </div>
                  <div className="divide-y divide-border">
                    {siteTasks?.length === 0 ? (
                      <div className="p-8 text-center"><p className="text-sm text-muted-foreground">No tasks for this site/date</p></div>
                    ) : siteTasks?.map(task => {
                      const worker = workers?.find(w => w?.id === task?.assigned_to_user_id);
                      return (
                        <div key={task?.id} className={`px-5 py-3.5 flex items-center gap-3 ${task?.status === 'completed' || task?.status === 'blocked' ? 'cursor-pointer hover:bg-muted/30' : ''} transition-colors`} onClick={() => (task?.status === 'completed' || task?.status === 'blocked') && setPhotoEvidenceTask(task)}>
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${task?.status === 'completed' ? 'bg-emerald-500' : task?.status === 'blocked' ? 'bg-red-500' : 'bg-amber-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{task?.title}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs text-muted-foreground">{worker?.full_name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS?.[task?.category]}`}>{task?.category}</span>
                              {task?.due_time && <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Icon name="Clock" size={10} />{task?.due_time}</span>}
                              {task?.status === 'completed' && task?.completed_at && (
                                <span className="text-xs text-emerald-600 flex items-center gap-0.5"><Icon name="CheckCircle2" size={10} />Completed by {task?.completed_by_user?.full_name || 'Unknown'} at {new Date(task?.completed_at)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${task?.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : task?.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{task?.status}</span>
                            {(task?.status === 'completed' || task?.status === 'blocked') && (
                              <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {photoEvidenceTask && (
        <TaskDetailModal
          task={photoEvidenceTask}
          onClose={() => setPhotoEvidenceTask(null)}
        />
      )}
    </>
  );
};

export default TaskPlanner;
